import React, { useEffect, useMemo, useRef, useState } from "react"
import { connect } from "react-redux"
import { withRouter } from "react-router-dom"
import { Card, CardBody, Container, Spinner } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import SearchBox from "components/Common/SearchBox"
import { capitalize } from "lodash"
import listColumns, { LEAD_TIMEZONE } from "./listColumns"
import FilterList from "./FilterList"
import { getClubsLeads, getClubLeadsLocations } from "./actions"
import { readLeadQuery, writeLeadQuery, leadDateError } from "./listQuery"
import "../../../assets/scss/custom/pages/_clubAccounts.scss"
import "../../../assets/scss/custom/pages/_clubBusinessLeads.scss"

const FILTER_LABELS = {
  search: "Search", location: "Location", sportType: "Sport", status: "Status",
  fromDate: "Created from", toDate: "Created to",
}
const filterLabel = (key, value) => `${FILTER_LABELS[key]}: ${key === "sportType" && value === "unspecified"
  ? "Not specified" : key === "status" || key === "sportType" ? capitalize(value) : value.replace(/^null, /, "")}`

export const ClubBusinessLeads = ({
  clubsLeads = {}, getClubsLeads, getClubLeadsLocations, clubLeadsLocations = [],
  listLoading = false, listError = "", locationsError = "", locationsLoading = false,
  settings, history, location,
}) => {
  const { filters, options } = useMemo(() => readLeadQuery(location.search), [location.search])
  const columns = useMemo(() => listColumns(location.search), [location.search])
  const [searchText, setSearchText] = useState(filters.search || "")
  const [advancedFilters, setAdvancedFilters] = useState(Boolean(filters.fromDate || filters.toDate))
  const searchTimer = useRef()
  const dateError = leadDateError(filters)
  const facetFilters = useMemo(() => ({
    ...(filters.sportType && { sportType: filters.sportType }),
    ...(filters.status && { status: filters.status }),
    ...(filters.fromDate && { fromDate: filters.fromDate }),
    ...(filters.toDate && { toDate: filters.toDate }),
  }), [filters.sportType, filters.status, filters.fromDate, filters.toDate])

  useEffect(() => {
    if (!dateError) getClubsLeads(filters, options)
  }, [filters, options, dateError, getClubsLeads])
  useEffect(() => {
    if (!dateError) getClubLeadsLocations(facetFilters)
  }, [facetFilters, dateError, getClubLeadsLocations])
  useEffect(() => {
    clearTimeout(searchTimer.current)
    setSearchText(filters.search || "")
    if (filters.fromDate || filters.toDate) setAdvancedFilters(true)
  }, [filters])
  useEffect(() => () => clearTimeout(searchTimer.current), [])

  const updateQuery = (nextFilters, nextOptions) => {
    const search = writeLeadQuery(nextFilters, nextOptions)
    if (search !== history.location.search) history.replace({ ...history.location, search })
  }
  const changeFilters = patch => {
    clearTimeout(searchTimer.current)
    updateQuery({ ...filters, search: searchText, ...patch }, { ...options, page: 1 })
  }
  const changeOptions = patch => {
    clearTimeout(searchTimer.current)
    const pendingSearch = searchText.trim() !== (filters.search || "")
    updateQuery({ ...filters, search: searchText }, {
      ...options, ...patch, ...(pendingSearch && { page: 1 }),
    })
  }
  const changeSearch = value => {
    setSearchText(value)
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      const current = readLeadQuery(history.location.search)
      updateQuery({ ...current.filters, search: value }, { ...current.options, page: 1 })
    }, 400)
  }
  // the „×”: the whole list at once, without the typing delay
  const clearSearch = () => {
    clearTimeout(searchTimer.current)
    setSearchText("")
    const current = readLeadQuery(history.location.search)
    updateQuery({ ...current.filters, search: "" }, { ...current.options, page: 1 })
  }
  const clearFilters = () => {
    clearTimeout(searchTimer.current)
    setSearchText("")
    updateQuery({}, { ...options, page: 1 })
  }

  const total = Number(clubsLeads.totalResults) || 0
  const totalPages = Math.max(1, Math.ceil(total / options.limit))
  const rows = clubsLeads.results || []
  const ready = !dateError && !listLoading && !listError && clubsLeads.totalResults != null
  const first = rows.length ? (options.page - 1) * options.limit + 1 : 0
  const last = rows.length ? first + rows.length - 1 : 0
  const activeFilters = Object.entries(filters)
  const dateFilterCount = [filters.fromDate, filters.toDate].filter(Boolean).length

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="User Accounts" breadcrumbItem="Club Business Leads" />
        <Card className="clubs-directory-card club-leads-directory">
          <CardBody className="p-0">
            <div className="club-directory-toolbar">
              <div className="club-directory-heading">
                <div>
                  <h4 className="mb-1">Club business leads {ready && <span className="text-muted">({total.toLocaleString()})</span>}</h4>
                  <p className="text-muted mb-0">Review club enquiries and open their details.</p>
                </div>
              </div>
              <div className="club-search-row">
                <SearchBox look="pill" className="club-search-box" aria-label="Search club leads" value={searchText}
                  maxLength={120} onValueChange={changeSearch} onClear={clearSearch}
                  placeholder="Search club, contact, #ID, email or phone" />
                <button type="button" className="btn btn-light club-more-filters"
                  aria-expanded={advancedFilters} aria-controls="lead-date-filters"
                  onClick={() => setAdvancedFilters(value => !value)}>
                  <i className="bx bx-slider-alt mr-1" aria-hidden="true" />
                  More filters {dateFilterCount > 0 && <span className="badge badge-primary ml-1">{dateFilterCount}</span>}
                </button>
              </div>
              <FilterList filters={filters} onChange={changeFilters} locations={clubLeadsLocations}
                settings={settings} advancedFilters={advancedFilters} sortBy={options.sortBy}
                onSort={sortBy => changeOptions({ sortBy, page: 1 })}
                locationsLoading={locationsLoading} dateError={dateError} />
              <p id="lead-timezone" className="text-muted font-size-12 mt-2 mb-0">
                Creation dates and times: {LEAD_TIMEZONE}. Date filters include both selected days.
              </p>
              {locationsError && <div className="text-muted mt-2" role="status">
                {locationsError} <button className="btn btn-link btn-sm" type="button"
                  onClick={() => getClubLeadsLocations(facetFilters)}>Retry locations</button>
              </div>}
              {(activeFilters.length > 0 || searchText) && <div className="club-active-filters">
                {activeFilters.map(([key, value]) => (
                  <button key={key} type="button" className="club-filter-chip"
                    aria-label={`Remove ${FILTER_LABELS[key]} filter`}
                    onClick={() => changeFilters({ [key]: "" })}>
                    {filterLabel(key, value)} <i className="bx bx-x" aria-hidden="true" />
                  </button>
                ))}
                <button className="btn btn-link btn-sm club-clear-filters" type="button" onClick={clearFilters}>Clear all</button>
              </div>}
            </div>

            {dateError ? <div className="lead-list-state text-danger" id="lead-date-error" role="alert">{dateError}</div>
              : listError ? <div className="lead-list-state" role="alert">
                <p>{listError}</p>
                <button className="btn btn-primary" type="button" onClick={() => getClubsLeads(filters, options)}>Retry leads</button>
              </div>
              : !ready ? <div className="lead-list-state" role="status"><Spinner size="sm" className="mr-2" />Loading club leads…</div>
              : rows.length === 0 ? <div className="lead-list-state" role="status">
                <p>{options.page > totalPages ? "This page is no longer available." : activeFilters.length
                  ? "No leads match the current filters." : "No club leads have been received yet."}</p>
                {options.page > 1 ? <button className="btn btn-light" type="button"
                  onClick={() => changeOptions({ page: 1 })}>Go to first page</button> :
                  activeFilters.length > 0 && <span className="text-muted">Adjust the filters or clear them to see all leads.</span>}
              </div> : <div className="table-responsive club-directory-table">
                <table className="table table-hover" aria-label="Club business leads">
                  <thead className="thead-light"><tr>{columns.map(column => <th scope="col" key={column.dataField}>{column.text}</th>)}</tr></thead>
                  <tbody>{rows.map(lead => <tr key={lead.id}>
                    {columns.map(column => <td key={column.dataField} data-label={column.text}>
                      {column.formatter(lead[column.dataField], lead)}
                    </td>)}
                  </tr>)}</tbody>
                </table>
              </div>}

            {ready && <div className="club-directory-footer">
              <div className="club-result-summary" role="status">{first}–{last} of {total.toLocaleString()} leads</div>
              <nav aria-label="Lead pages" className="lead-pagination">
                <button type="button" className="btn btn-light btn-sm" aria-label="Previous page"
                  disabled={options.page <= 1} onClick={() => changeOptions({ page: options.page - 1 })}>
                  <i className="bx bx-chevron-left" aria-hidden="true" />
                </button>
                <span>Page {options.page} of {totalPages}</span>
                <button type="button" className="btn btn-light btn-sm" aria-label="Next page"
                  disabled={options.page >= totalPages} onClick={() => changeOptions({ page: options.page + 1 })}>
                  <i className="bx bx-chevron-right" aria-hidden="true" />
                </button>
              </nav>
              <label className="club-page-size mb-0">Rows
                <select className="custom-select custom-select-sm" value={options.limit}
                  onChange={event => changeOptions({ limit: Number(event.target.value), page: 1 })}>
                  {[25, 50, 100].map(size => <option key={size} value={size}>{size}</option>)}
                </select>
              </label>
            </div>}
          </CardBody>
        </Card>
      </Container>
    </div>
  )
}

const mapStateToProps = ({ clubsLeads = {}, session = {} }) => ({
  clubsLeads: clubsLeads.clubsLeads,
  clubLeadsLocations: clubsLeads.clubLeadsLocations,
  listLoading: clubsLeads.listLoading,
  listError: clubsLeads.listError,
  locationsLoading: clubsLeads.locationsLoading,
  locationsError: clubsLeads.locationsError,
  settings: session.settings,
})

export default connect(mapStateToProps, { getClubsLeads, getClubLeadsLocations })(withRouter(ClubBusinessLeads))
